import { bbAxios, signalActivity } from '@bb/session-manager';

import { KJUR } from 'jsrsasign';
import { ONLYOFFICE_BB_LOGO } from '../config';
import {
  LOG_ENDPOINT,
  ONLYOFFICE_CONFIG_ENDPOINT,
  ONLYOFFICE_TRACK_ENDPOINT,
} from '../endpoints.config';

const createDownloadRef = (docName, data) => {
  const link = document.createElement('a');
  link.download = docName;
  link.href = data;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// TODO: Add back in when ready for document version history
// const onRequestHistory = (event, fileVersion, onlyOfficeHistory) => {
//     // TODO: call server to get fresh history
//     window.DocsAPICopy.refreshHistory({ currentVersion: fileVersion, history: onlyOfficeHistory });
// };

const onRequestHistoryData = (event, onlyOfficeHistoryData) => {
  const version = event.data;
  try {
    window.DocsAPICopy.setHistoryData(onlyOfficeHistoryData[version - 1]);
  } catch (ex) {
    console.error('Error in RequestHistoryData', ex);
  }
};

const onDocumentStateChange = (event) => {
  // the document is modified
  const title = document.title.replace(/\*$/g, '');
  document.title = title + (event.data ? '*' : '');

  // Signal user activity to session manager when document state changes
  // This ensures the session doesn't expire while user is actively editing in OnlyOffice
  signalActivity();

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('userActivity', { detail: { source: 'onlyoffice' } })
    );
  }
};

/**
 * Called when document is ready - signals user activity
 */
const onDocumentReady = () => {
  // Signal user activity to session manager when document loads
  signalActivity();

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('userActivity', { detail: { source: 'onlyoffice-ready' } })
    );
  }
};

const onOutdatedVersion = () => {
  // the document is opened for editing with the old document.key value
  window.location.reload();
};

/**
 * @param {string|undefined} bearer - MUST be an ID token, not an access token, when
 *   present (it is undefined only for an unauthenticated caller). It is embedded in
 *   the OnlyOffice callbackUrl below, and document-server posts that URL back to
 *   bbcedit-web, which resolves it via /na/db-connection-uri. That endpoint reads the
 *   `email` claim by decoding the JWT without validating it (deliberately, so a long
 *   editing session with an expired token still saves). Cognito access tokens have no
 *   `email` claim, so passing one makes the callback 400 and the editor report
 *   "The document could not be saved".
 */
export const combineConfig = (
  styles,
  contractInfo,
  onlyofficeConfig,
  docOrchUrl,
  defaultEditorName,
  bearer
) => {
  let result = {};
  const docName = contractInfo.contract.name;
  const { userId, email, roles } = contractInfo.user;
  const { _id: id } = contractInfo.contract;
  const userEditorName = defaultEditorName || email;
  const groups = contractInfo.contract.groups
    ? contractInfo.contract.groups.map(({ _id }) => _id)
    : [];
  const version = 1;
  const ext = contractInfo.contract.path.split('.').slice(-1)[0];
  const isLocal = window.location.host.includes('localhost');
  const company = isLocal ? 'abccorp' : window.location.host.split('.')[0];
  const internalUrl = isLocal
    ? 'http://host.docker.internal:3000'
    : `http://bbcedit-web.${window.location.host.split('.')[0]}.svc.cluster.local`;
  const fileUrl = `${docOrchUrl}/files/${company}/${id}/${version}.${ext}`;
  const callbackUrl = `${internalUrl}${ONLYOFFICE_TRACK_ENDPOINT}?user=${
    email
  }&filename=${id}.${version}.${ext}&company=${company}&groups=${groups}&userId=${
    userId
  }&roles=${roles}&authorization=BEARER%20${bearer}`;
  result = {
    height: styles.height,
    document: {
      fileType: ext,
      title: docName,
      key: onlyofficeConfig.editor.key,
      url: fileUrl,
      permissions: {
        download: true,
        edit: true,
        print: false,
        review: true,
        chat: false,
      },
    },
    documentType: 'word',
    editorConfig: {
      user: {
        id: userId,
        name: userEditorName,
      },
      embedded: {
        saveUrl: fileUrl,
        embedUrl: fileUrl,
        shareUrl: fileUrl,
        toolbarDocked: 'top',
      },
      customization: {
        review: {
          showReviewChanges: true,
          hoverMode: true,
          reviewDisplay: 'markup',
          trackChanges: true,
        },
        comments: true,
        autosave: true,
        compactToolbar: true,
        forcesave: true,
        customer: {
          info: 'v6.1.0',
          logo: ONLYOFFICE_BB_LOGO,
          mail: 'support@blackboiler.com',
          name: 'Daniel Broderick',
          www: 'www.blackboiler.com',
        },
        logo: {
          image: ONLYOFFICE_BB_LOGO,
          imageEmbedded: ONLYOFFICE_BB_LOGO,
          url: 'https://www.blackboiler.com',
        },
        uiTheme: 'default-light',
      },
      wopi: {
        enable: true,
      },
      callbackUrl: `${callbackUrl}`,
    },
    events: {
      onDownloadAs(event) {
        createDownloadRef(docName, event.data.url);
      },
      // TODO: re-enable this to display "show version history"
      // onRequestHistory(event) {
      //     onRequestHistory(event, onlyofficeConfig.file.version, onlyofficeConfig.history);
      // },
      onRequestHistoryData(event) {
        onRequestHistoryData(event, onlyofficeConfig.historyData);
      },
      onDocumentStateChange(event) {
        onDocumentStateChange(event);
      },
      onDocumentReady() {
        onDocumentReady();
      },
      onOutdatedVersion() {
        onOutdatedVersion();
      },
    },
  };

  return result;
};

export const toJWT = (payload, key) => {
  const sHeader = JSON.stringify({ alg: 'HS256', typ: 'JWT' });
  const sPayload = JSON.stringify(payload);
  const sJWT = KJUR.jws.JWS.sign('HS256', sHeader, sPayload, key);
  return sJWT;
};

export const appendScript = (scriptToAppend) => {
  const script = document.createElement('script');
  script.src = scriptToAppend;
  script.async = true;
  document.body.appendChild(script);
};

export const logDocumentExport = (contractId) => {
  const body = {
    message: `${contractId}: Exported`,
    action: 'export',
    documentId: contractId,
  };
  return bbAxios
    .post(`${LOG_ENDPOINT}`, body)
    .then((resp) => {
      console.info('Successfully logged document export with data:', resp.data);
      // Store optimistic export state so the Contracts page can show the
      // checkmark immediately, even if the read-replica hasn't caught up yet.
      try {
        const key = 'bb-recent-exports';
        const existing = JSON.parse(sessionStorage.getItem(key) || '{}');
        existing[contractId] = new Date().toISOString();
        sessionStorage.setItem(key, JSON.stringify(existing));
      } catch { /* sessionStorage may be unavailable */ }
      return resp;
    })
    .catch((err) => {
      console.error('Failed to log document export with error:', err);
      throw err; // Re-throw so callers know the export log failed
    });
};

/**
 * Ask document-server to flush the in-editor changes to storage before an export.
 *
 * The command body must be signed with the OnlyOffice shared secret: with
 * token.enable on, document-server rejects an unsigned command and logs
 * "checkJwt error: invalid algorithm". Until this was signed the flush silently
 * never happened, so an export could ship without the user's most recent edits --
 * the failure only reached console.error, never the user.
 *
 * Mirrors the ConvertService call in DocServerReadOnly.tsx, which already signs.
 */
export const forceSaveRequest = async (onlyOfficeUrl, contractId, onlyOfficeTokenKey) => {
  if (!onlyOfficeUrl || !onlyOfficeTokenKey) {
    console.error('forceSaveRequest: missing onlyOfficeUrl or onlyOfficeTokenKey');
    return undefined;
  }

  const onlyOfficeEditorKey = await bbAxios
    .get(`${ONLYOFFICE_CONFIG_ENDPOINT}?documentId=${contractId}`)
    .then(({ data }) => {
      if (data && data.editor && data.editor.key) {
        return data.editor.key;
      }
      throw Error(`Invalid only office configuration object: ${data}`);
    })
    .catch((err) => {
      console.error(
        `Failed to retrieve only office configuration details for contract ID ${contractId} with error:`,
        err
      );
    });

  const onlyOfficeCommandUri = `${onlyOfficeUrl}/coauthoring/CommandService.ashx`;

  const body = {
    c: 'forcesave',
    key: onlyOfficeEditorKey,
  };
  const asToken = toJWT(body, onlyOfficeTokenKey);
  return bbAxios
    .post(onlyOfficeCommandUri, { ...body, token: asToken })
    .then((res) => {
      if (res.data && res.data.error) {
        const code = res.data.error;
        throw Error(`Failed to force save request with code: ${code}`);
      }
    })
    .catch((err) => {
      console.error('Failed to log document export with error:', err);
    });
};

// may be needed in future: https://github.com/BlackBoiler/bbcedit_web/blob/3f8cd5162c9657f4123d1f9d53d0731524e088c2/drywall/client/src/app/onlyoffice/onlyoffice.service.js#L191-L194
// export const removeInsertImageFromUrl = () => {
//     const $iframe = $('iframe')
//     $('#tlbtn-insertimage > ul > li:nth-child(2)', $iframe.contents()).remove()
//   }
